import { Icon, IconName, Separator } from '@usealloy/component-library';
import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import ErrorBoundary from '~/components/ErrorBoundary';
import { RULE_LIBRARY_ORGANIZATION_SLUG } from '~/constants';
import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';

import { Sidebar } from '../component/Sidebar';
import { SIDEBAR_MOBILE_QUERY, useMediaQuery } from '../component/hooks/useMediaQuery';
import type { MobileDrawerControlProps } from '../component/types';
import { AdminGroup } from './NavList.admin';
import { PoliciesGroup } from './NavList.policies';
import { RecordsGroup } from './NavList.records';
import { ReportsGroup } from './NavList.reports';
import { ReviewGroup } from './NavList.review';
import { SettingsGroup } from './NavList.settings';
import { isJourneyActive, isPrefixActive } from './routeMatchers';
import { CustomerSwitcher } from './NavList.customerSwitcher';
import { AgentProfileMenuItem } from './AgentProfileMenuItem';
import { PermissionChecker } from '~/helpers/components/PermissionChecker';
import { getIsParentAgentAccessingChildCustomer } from '~/dashboard/helpers';

// ============================================================
//     TYPES
// ============================================================
export interface SideNavProps extends MobileDrawerControlProps {
  /**
   * Controlled expanded state (pair with `onExpandedChange`).
   *
   * At the mount point this is wired to `useExpandingSidebar` so the expand state
   * shares the `sidebarExpanded` localStorage key (and the content-area layout class)
   * with the legacy sidebar.
   */
  expanded?: boolean;
  /**
   * Callback fired when the sidebar expand state changes.
   */
  onExpandedChange?: (expanded: boolean) => void;
}

// ============================================================
//     HELPERS
// ============================================================
/**
 * The ungrouped Home item rendered above the labeled groups (FE2.0 has no Home).
 */
const HomeGroup = () => {
  const { pathname } = useLocation();

  return (
    <Sidebar.Group>
      <Sidebar.Menu>
        <Sidebar.MenuItem>
          <Sidebar.MenuButton
            as="a"
            data-dd-action-name="alloydd-sidebar-nav-home"
            href={`${window.location.origin}${routes.v3.dashboard.home.dashboard}`}
            isActive={isJourneyActive(
              routes.v3.dashboard.home.dashboard,
              pathname,
            )}
            tooltip="Home"
          >
            <Icon icon={IconName.Home} size="small" />
            <span className="fs-unmask">Home</span>
          </Sidebar.MenuButton>
        </Sidebar.MenuItem>
      </Sidebar.Menu>
    </Sidebar.Group>
  );
};

const AnalyticsMenuItem = () => {
  const { pathname } = useLocation();

  return (
    <Sidebar.MenuItem>
      <Sidebar.MenuButton
        as="a"
        data-dd-action-name="alloydd-sidebar-nav-analytics"
        href={`${window.location.origin}${routes.v3.dashboard.home.dashboard}`}
        isActive={isJourneyActive(routes.v3.dashboard.home.dashboard, pathname)}
        tooltip="Analytics"
      >
        <Icon icon={IconName.ChartColumn} size="large" />
        <span className="fs-unmask">Analytics</span>
      </Sidebar.MenuButton>
    </Sidebar.MenuItem>
  );
};

/**
 * The flat "Settings" link, rendered above the Settings group (Notifications,
 * Resources) for every account type — including rule-library accounts, which
 * otherwise only see Home.
 */
const SettingsMenuItem = () => {
  const { agent, customer, featureFlags } = useContext(UserContext);
  const { pathname } = useLocation();

  const isParentAgentAccessingChildCustomer = getIsParentAgentAccessingChildCustomer({
    agent,
    customer,
  });

  if (isParentAgentAccessingChildCustomer) {
    return null;
  }

  return (
    <PermissionChecker
      permissions={featureFlags?.afefAuditPermissions ? ['READ:Settings:null'] : []}
    >
      <Sidebar.MenuItem>
        <Sidebar.MenuButton
          as="a"
          data-dd-action-name="alloydd-sidebar-nav-settings"
          href={`${window.location.origin}${routes.v3.dashboard.settings.index}`}
          isActive={isPrefixActive(routes.v3.dashboard.settings.index, pathname)}
          tooltip="Settings"
        >
          <Icon icon={IconName.Settings} size="large" />
          <span className="fs-unmask">Settings</span>
        </Sidebar.MenuButton>
      </Sidebar.MenuItem>
    </PermissionChecker>
  );
};

/**
 * Fallback shown in place of the nav groups if they fail to render.
 *
 * Rendered inside `Sidebar.Body`, so it inherits the rail's fixed width/collapse
 * behavior instead of the page-oriented default `ErrorBoundary` fallback.
 */
const NavErrorFallback = () => (
  <Sidebar.Body>
    <Sidebar.Group>
      <Sidebar.Menu>
        <Sidebar.MenuItem>
          <Sidebar.MenuButton disabled tooltip="Navigation unavailable">
            <Icon icon={IconName.Warning} size="small" />
            <span className="fs-unmask">Navigation unavailable</span>
          </Sidebar.MenuButton>
        </Sidebar.MenuItem>
      </Sidebar.Menu>
    </Sidebar.Group>
  </Sidebar.Body>
);

// ============================================================
//     COMPONENT
// ============================================================
/**
 * The Sidebar_v2 navigation for the legacy dashboard.
 *
 * Backports the FE2.0 SideNav's structure (Review / Explore / Policies / Settings
 * groups) onto Daffy's existing routes, permissions, and feature flags — a bridge
 * while customers transition between the legacy dashboard and the beta dashboard.
 */
export const SideNav = (props: SideNavProps) => {
  const {
    expanded, isOpenMobile, onExpandedChange, onOpenMobileChange,
  } = props;

  const { customer, featureFlags } = useContext(UserContext);
  const { pathname } = useLocation();
  const isMobileScreenSize = useMediaQuery(SIDEBAR_MOBILE_QUERY);

  const inAdminDashboard = pathname.startsWith('/v3/dashboard/admin');
  const isRuleLibraryAccount = customer?.organizationNameSlug === RULE_LIBRARY_ORGANIZATION_SLUG;
  const isOnBetaSettingsPage = pathname.includes('/settings/beta-features');

  // Close the mobile drawer whenever the route changes (e.g. after clicking a nav link).
  useEffect(() => {
    onOpenMobileChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on navigation
  }, [pathname]);

  const getContent = () => {
    // Admin dashboard
    if (inAdminDashboard) {
      return (
        <Sidebar.Body>
          <AdminGroup />
        </Sidebar.Body>
      );
    }

    // Rule-library accounts only see Home and Settings
    if (isRuleLibraryAccount) {
      return (
        <>
          <HomeGroup />
          <Separator />
          <SettingsMenuItem />
          <SettingsGroup />
        </>
      );
    }

    // Default to Home and Review, Explore, Policies groups
    return (
      <>
        {/* Mobile drawer is always full-width, so the switcher should always render
            in its expanded look there, regardless of the desktop rail's own collapse state. */}
        <CustomerSwitcher expanded={expanded || isMobileScreenSize} />
        <Sidebar.Body>
          <AnalyticsMenuItem />
          <ReviewGroup />
          <ReportsGroup />
          <RecordsGroup />
          <PoliciesGroup />
          <Separator />
          <SettingsMenuItem />
          <SettingsGroup />
        </Sidebar.Body>
      </>
    );
  };

  return (
    <Sidebar
      aria-label="Main navigation"
      expanded={expanded}
      isCollapsible
      isMobileEnabled
      isOpenMobile={isOpenMobile}
      keyboardShortcut={{ key: 'b' }}
      onExpandedChange={onExpandedChange}
      onOpenMobileChange={onOpenMobileChange}
      role="navigation"
      shouldShowMobile={isMobileScreenSize}
      style={{ backgroundColor: 'var(--cl-gray-100)' }}
      variant="primary"
      {...Boolean(!featureFlags?.fepSidenavDefaultOff)
        && !isOnBetaSettingsPage
        && { 'data-pendo-anchor': 'beta-sidenav-default-on' }}
    >
      <Sidebar.ExpandToggle data-dd-action-name="alloydd-sidebar-nav-expanded-toggle" />
      <ErrorBoundary errorChildren={<NavErrorFallback />}>
        {getContent()}
      </ErrorBoundary>
      <Sidebar.Footer style={{ padding: 0 }}>
        <AgentProfileMenuItem expanded={expanded} />
      </Sidebar.Footer>
    </Sidebar>
  );
};
